import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Plus, Trash2, AlertCircle, Loader2, CheckCircle, ChevronDown, ChevronUp, Info } from 'lucide-react';
import LocationSearch from '@/components/LocationSearch';
import { initiateServicePayment } from '@/utils/razorpay';
import { getUser } from '@/utils/auth';
import ModuleConfigurator from '@/components/interior/ModuleConfigurator';
import WardrobeLayoutPreview from '@/components/interior/WardrobeLayoutPreview';

const API_URL = '';
const DEFAULT_CONSULTATION_FEE = 2999;

const TIERS = [
  {
    key: 'minimalist',
    tier: 1,
    label: 'Minimalist',
    tag: 'Tier 1',
    desc: 'Clean, functional spaces that prioritise utility and a calm, uncluttered aesthetic.',
    color: 'stone',
    price_indicator: '₹',
    badge: null,
    appliance_support: 'Supports basic appliance integration',
    inclusions: [
      'Essential modular furniture for daily use',
      'Neutral palette with calm finishes',
      'Standard hardware with reliable durability',
      'Functional lighting layout',
      'Optimised storage in core rooms',
      'Standard wardrobe and kitchen configuration',
    ],
    upgrades: [],
  },
  {
    key: 'modern',
    tier: 2,
    label: 'Modern',
    tag: 'Tier 2',
    desc: 'Contemporary, well-finished spaces designed for everyday comfort and a polished look.',
    color: 'emerald',
    price_indicator: '₹₹',
    badge: 'Most Popular',
    appliance_support: 'Supports branded appliances (mid-range options)',
    inclusions: [
      'Contemporary modular furniture across all key rooms',
      'Premium finishes with refined detailing',
      'Smooth-action hardware for everyday comfort',
      'Layered lighting with accent zones',
      'Smarter storage and organised wardrobes',
      'Stylised kitchen layout with upgraded surfaces',
      'Mid-range appliance compatibility',
    ],
    upgrades: [
      'Upgraded finishes and detailing over Minimalist',
      'Improved hardware feel and longevity',
      'Better lighting design with mood layers',
      'More refined modular configurations',
    ],
  },
  {
    key: 'traditional',
    tier: 3,
    label: 'Traditional',
    tag: 'Tier 3',
    desc: 'Rich, character-driven interiors with crafted detailing and a warm, premium feel.',
    color: 'amber',
    price_indicator: '₹₹₹',
    badge: null,
    appliance_support: 'Supports premium appliances and built-in configurations',
    inclusions: [
      'Crafted modular furniture with detailed profiles',
      'Warm, premium finishes with texture variation',
      'Decorative hardware with refined craftsmanship',
      'Architectural lighting and feature highlights',
      'Custom storage solutions for full home',
      'Built-in configurations for kitchen and wardrobes',
      'Statement design elements in living areas',
    ],
    upgrades: [
      'Crafted detailing and richer aesthetic over Modern',
      'Premium finish quality and material variety',
      'Built-in furniture options and integrated storage',
      'Upgraded lighting design with feature pieces',
      'Wider scope for personalisation per room',
    ],
  },
  {
    key: 'luxury',
    tier: 4,
    label: 'Luxury',
    tag: 'Tier 4',
    desc: 'A bespoke, high-end design experience with premium materials and smart-home integration.',
    color: 'violet',
    price_indicator: '₹₹₹₹',
    badge: 'Top-End Experience',
    appliance_support: 'Supports high-end / imported appliances and smart integrations',
    inclusions: [
      'Bespoke modular furniture across the entire home',
      'Top-grade finishes with luxury aesthetics',
      'Premium imported hardware and fittings',
      'Architectural and ambient lighting design',
      'Smart-home automation readiness',
      'Custom-built kitchen with high-end configurations',
      'Concierge-style design personalisation',
    ],
    upgrades: [
      'Elevated material grade and finish quality over Traditional',
      'Bespoke design across every room',
      'Smart-home automation and lighting controls',
      'Premium imported hardware and fittings',
      'Dedicated personalisation throughout the project',
    ],
  },
];

const KITCHEN_LAYOUTS = [
  { key: 'Straight', label: 'Straight', desc: 'Single wall' },
  { key: 'L', label: 'L-Shape', desc: 'Two perpendicular runs' },
  { key: 'U', label: 'U-Shape', desc: 'Three runs' },
  { key: 'Parallel', label: 'Parallel', desc: 'Two facing runs' },
];

const ROOM_TYPES = [
  { key: 'kitchen', label: 'Kitchen' },
  { key: 'bedroom', label: 'Bedroom' },
  { key: 'living_room', label: 'Living Room' },
  { key: 'pooja_room', label: 'Pooja Room' },
  { key: 'bathroom', label: 'Bathroom' },
  { key: 'dining_room', label: 'Dining Room' },
  { key: 'study_room', label: 'Study Room' },
  { key: 'foyer', label: 'Foyer / Entrance' },
  { key: 'other', label: 'Other' },
];

const ROOM_MODULE_MAP = {
  kitchen: [{ key: 'kitchen', label: 'Kitchen' }],
  bedroom: [
    { key: 'wardrobe', label: 'Wardrobe' },
    { key: 'tv_unit', label: 'TV Unit' },
    { key: 'study_desk', label: 'Study Desk' },
  ],
  living_room: [
    { key: 'tv_unit', label: 'TV Unit' },
    { key: 'feature_wall', label: 'Feature Wall' },
    { key: 'crockery_unit', label: 'Crockery Unit' },
    { key: 'shelves', label: 'Shelves' },
    { key: 'bar_unit', label: 'Bar Unit' },
  ],
  pooja_room: [{ key: 'pooja_unit', label: 'Pooja Unit' }],
  bathroom: [{ key: 'vanity_unit', label: 'Vanity Unit' }],
  dining_room: [
    { key: 'crockery_unit', label: 'Crockery Unit' },
    { key: 'bar_unit', label: 'Bar Unit' },
  ],
  study_room: [
    { key: 'study_desk', label: 'Study Desk' },
    { key: 'wardrobe', label: 'Wardrobe' },
    { key: 'shelves', label: 'Shelves' },
  ],
  foyer: [
    { key: 'shoe_rack', label: 'Shoe Rack' },
    { key: 'feature_wall', label: 'Feature Wall' },
  ],
  other: [
    { key: 'wardrobe', label: 'Wardrobe' },
    { key: 'tv_unit', label: 'TV Unit' },
    { key: 'study_desk', label: 'Study Desk' },
    { key: 'crockery_unit', label: 'Crockery Unit' },
    { key: 'shoe_rack', label: 'Shoe Rack' },
    { key: 'bar_unit', label: 'Bar Unit' },
    { key: 'vanity_unit', label: 'Vanity Unit' },
    { key: 'pooja_unit', label: 'Pooja Unit' },
    { key: 'feature_wall', label: 'Feature Wall' },
    { key: 'shelves', label: 'Shelves' },
  ],
};

const inferRoomType = (name) => {
  const n = (name || '').toLowerCase();
  if (n.includes('kitchen')) return 'kitchen';
  if (n.includes('bedroom') || n.includes('master bed')) return 'bedroom';
  if (n.includes('living')) return 'living_room';
  if (n.includes('pooja') || n.includes('prayer') || n.includes('mandir')) return 'pooja_room';
  if (n.includes('bathroom') || n.includes('toilet') || n.includes('washroom')) return 'bathroom';
  if (n.includes('dining')) return 'dining_room';
  if (n.includes('study') || n.includes('office')) return 'study_room';
  if (n.includes('foyer') || n.includes('entrance') || n.includes('entry')) return 'foyer';
  return 'other';
};

const QUICK_ROOMS = [
  { name: 'Kitchen', type: 'kitchen' },
  { name: 'Master Bedroom', type: 'bedroom' },
  { name: 'Bedroom', type: 'bedroom' },
  { name: 'Living Room', type: 'living_room' },
  { name: 'Pooja Room', type: 'pooja_room' },
];

const PROJECT_ADDONS = [
  { key: 'painting', label: 'Painting', desc: 'Interior wall painting based on carpet area' },
  { key: 'electrical', label: 'Electrical & Lighting (Provisional)', desc: 'Basic wiring + standard switches at \u20B975/sq.ft. Final costing done at design consultation.' },
  { key: 'civil_work', label: 'Civil Work', desc: 'Demolition, masonry, plumbing modifications' },
  { key: 'home_automation', label: 'Home Automation', desc: 'Smart switches, sensors, automation hub' },
];

const INR = (v) => v != null ? `\u20B9${v.toLocaleString('en-IN')}` : '\u20B90';

const InteriorDesignZEngine = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [consultationFee, setConsultationFee] = useState(DEFAULT_CONSULTATION_FEE);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [moduleOptions, setModuleOptions] = useState(null);
  const [bookingPayment, setBookingPayment] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);

  // OTP verification state (driven by server-side `otp_verification_enabled` feature flag)
  const [otpRequired, setOtpRequired] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpVerifiedPhone, setOtpVerifiedPhone] = useState('');

  // Lead-capture mode: when `instant_quote_enabled` is OFF, the screen collects
  // a request instead of revealing prices on-screen.
  const [instantQuoteEnabled, setInstantQuoteEnabled] = useState(true);
  const [contactInfo, setContactInfo] = useState(null);
  const [requestResult, setRequestResult] = useState(null);

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', city: '',
    property_type: 'residential',
    variant: 'modern',
    carpet_area: '',
    false_ceiling_sqft: '',
    project_addons: { painting: false, electrical: false, civil_work: false, home_automation: false },
    rooms: [],
  });
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch(`${API_URL}/api/services-pricing/interior-design`);
        if (res.ok) { const d = await res.json(); if (d.consultation_fee?.price) setConsultationFee(d.consultation_fee.price); }
      } catch { /* ignore */ } finally { setPricingLoading(false); }
    };
    fetchPricing();
  }, []);

  // Load feature-flag state once so the OTP step only appears when admins enable it.
  useEffect(() => {
    const fetchFlag = async () => {
      try {
        const r = await fetch(`${API_URL}/api/config?portal=consumer`);
        if (!r.ok) return;
        const d = await r.json();
        setOtpRequired(Boolean(d?.feature_flags?.otp_verification_enabled));
        setInstantQuoteEnabled(Boolean(d?.feature_flags?.instant_quote_enabled));
        if (d?.contact) setContactInfo(d.contact);
      } catch { /* ignore */ }
    };
    fetchFlag();
  }, []);

  // Invalidate OTP verification if the user edits the phone after verifying.
  useEffect(() => {
    if (otpVerified && formData.phone && otpVerifiedPhone) {
      const current = formData.phone.replace(/\D/g, '').slice(-10);
      if (current !== otpVerifiedPhone) {
        setOtpVerified(false);
        setOtpSent(false);
        setOtpCode('');
        setOtpVerifiedPhone('');
      }
    }
  }, [formData.phone, otpVerified, otpVerifiedPhone]);

  useEffect(() => {
    if (!moduleOptions) {
      fetch(`${API_URL}/api/interior-design/module-options`)
        .then(r => r.json()).then(d => setModuleOptions(d)).catch(() => {});
    }
  }, [moduleOptions]);

  const addRoom = (name, type) => {
    const roomType = type || inferRoomType(name || '');
    setFormData(prev => ({
      ...prev,
      rooms: [...prev.rooms, { name: name || `Room ${prev.rooms.length + 1}`, room_type: roomType, modules: [], panels: [], addons: [] }],
    }));
  };

  const removeRoom = (idx) => {
    setFormData(prev => ({ ...prev, rooms: prev.rooms.filter((_, i) => i !== idx) }));
  };

  const updateRoom = (idx, field, value) => {
    setFormData(prev => {
      const rooms = [...prev.rooms];
      rooms[idx] = { ...rooms[idx], [field]: value };
      return { ...prev, rooms };
    });
  };

  const toggleAddon = (key) => {
    setFormData(prev => ({
      ...prev,
      project_addons: { ...prev.project_addons, [key]: !prev.project_addons[key] },
    }));
  };

  const addKitchenToRoom = (roomIdx) => {
    const room = formData.rooms[roomIdx];
    const hasKitchen = room.modules.some(m => m.type === 'kitchen');
    if (hasKitchen) { toast.error('Room already has a kitchen module'); return; }
    const kitchenMod = {
      type: 'kitchen', layout: 'L', lengths: [8, 6], height: 7, depth: 2,
      countertop: 'Basic_Granite', loft: false, num_tall_units: 0,
      quality_tier: 'standard',
    };
    updateRoom(roomIdx, 'modules', [...room.modules, kitchenMod]);
  };

  const normalizePhone = (raw) => {
    const digits = (raw || '').replace(/\D/g, '');
    let local = digits;
    if (local.startsWith('91') && local.length === 12) local = local.slice(2);
    if (local.startsWith('0') && local.length === 11) local = local.slice(1);
    return local;
  };

  const handleSendOtp = async () => {
    const local = normalizePhone(formData.phone);
    const phoneOk = local.length === 10 && /^[6-9]/.test(local) && !/^(\d)\1{9}$/.test(local);
    if (!phoneOk) { toast.error('Please enter a valid 10-digit Indian mobile number to receive your code.'); return; }
    setOtpSending(true);
    try {
      const r = await fetch(`${API_URL}/api/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: local, purpose: 'interior_quote' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || 'Couldn\'t send the code. Please try again.'); return; }
      if (d.skipped) { toast.success('No verification needed right now &mdash; you\'re good to go.'); setOtpVerified(true); setOtpVerifiedPhone(local); return; }
      setOtpSent(true);
      setOtpVerifiedPhone(local);
      // Only surface the dev OTP in non-production environments.
      const env = (window?.ZEN_CONFIG?.environment || '').toLowerCase();
      if (d?._mock_otp && env !== 'production' && env !== 'prod') {
        toast.success(`Code sent (dev): ${d._mock_otp}`);
      } else {
        toast.success('Code sent to your phone.');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally { setOtpSending(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode || otpCode.length < 4) { toast.error('Please enter the code we sent you.'); return; }
    const local = otpVerifiedPhone || normalizePhone(formData.phone);
    setOtpVerifying(true);
    try {
      const r = await fetch(`${API_URL}/api/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: local, otp_code: otpCode, purpose: 'interior_quote' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.verified) { toast.error(d?.message || d?.detail || 'That code didn\'t match. Please try again.'); return; }
      setOtpVerified(true);
      toast.success('You\'re verified &mdash; thanks!');
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally { setOtpVerifying(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rooms.length === 0) { toast.error('Add at least one room'); return; }
    const hasModules = formData.rooms.some(r => r.modules.length > 0);
    if (!hasModules) { toast.error('Add at least one module to a room'); return; }

    // ---- Client-side contact validation (mirrors backend /v3/quote) ----
    const name = (formData.name || '').trim();
    if (name.length < 2) { toast.error('Please enter your full name.'); return; }

    const emailOk = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/.test((formData.email || '').trim());
    if (!emailOk) { toast.error('Please enter a valid email address.'); return; }

    const digits = (formData.phone || '').replace(/\D/g, '');
    let local = digits;
    if (local.startsWith('91') && local.length === 12) local = local.slice(2);
    if (local.startsWith('0') && local.length === 11) local = local.slice(1);
    const phoneOk = local.length === 10 && /^[6-9]/.test(local) && !/^(\d)\1{9}$/.test(local);
    if (!phoneOk) { toast.error('Please enter a valid 10-digit Indian mobile number.'); return; }

    // OTP gate — required when admin has enabled `otp_verification_enabled`.
    if (otpRequired && !otpVerified) {
      toast.error('Verify to unlock your full quote.');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name, email: formData.email.trim().toLowerCase(), phone: local,
        city: formData.city || 'Hyderabad', property_type: formData.property_type,
        variant: formData.variant,
        design_style: formData.variant,
        carpet_area: formData.carpet_area ? parseFloat(formData.carpet_area) : 0,
        false_ceiling_sqft: formData.false_ceiling_sqft ? parseFloat(formData.false_ceiling_sqft) : 0,
        project_addons: formData.project_addons,
        rooms: formData.rooms,
      };

      // Lead-capture mode: save the request, no on-screen pricing.
      if (!instantQuoteEnabled) {
        const res = await fetch(`${API_URL}/api/interior-design/v3/quote-request`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Could not submit your request'); }
        const data = await res.json();
        setRequestResult({ ...data, name, email: payload.email, phone: local });
        setStep(3);
        toast.success('Request received! We\u2019ll email your quote shortly.');
        return;
      }

      const res = await fetch(`${API_URL}/api/interior-design/v3/quote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Quote generation failed'); }
      const data = await res.json();
      setQuote(data);
      setStep(2);
      toast.success('Your quote is ready!');
    } catch (error) {
      toast.error(error.message || 'Failed to generate quote');
    } finally { setLoading(false); }
  };

  // =============================================
  // STEP 3: Lead-capture confirmation (instant_quote_enabled = OFF)
  // =============================================
  if (step === 3 && requestResult) {
    const whatsappNumber = contactInfo?.whatsapp_number || '918790460259';
    const supportTel = contactInfo?.support_phone_tel || '+918790460259';
    const supportPhone = contactInfo?.support_phone || '+91 87904 60259';
    const roomCount = formData.rooms.length;
    const waText = encodeURIComponent(
      `Hi ZenStudios! I just submitted an interior design quote request.\n` +
      `Request ID: ${requestResult.request_id}\n` +
      `Name: ${requestResult.name}\n` +
      `Phone: ${requestResult.phone}\n` +
      `City: ${formData.city || 'Hyderabad'}\n` +
      `Style: ${formData.variant}\n` +
      `Rooms: ${roomCount}\n` +
      `Please share my quote.`
    );
    const waUrl = `https://wa.me/${whatsappNumber}?text=${waText}`;
    return (
      <div className="min-h-screen bg-white py-32" data-testid="quote-request-confirmation">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-6">
            <CheckCircle className="w-9 h-9 text-emerald-700" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-3 tracking-tight">Request received</h1>
          <p className="text-base text-gray-500 mb-2" data-testid="request-id-label">
            Request ID: <span className="font-mono font-semibold text-gray-800">{requestResult.request_id}</span>
          </p>
          <p className="text-base text-gray-600 max-w-xl mx-auto mb-8">
            Thanks, {requestResult.name}! Our design team is preparing your personalised quote and will
            email it to <span className="font-semibold text-gray-800">{requestResult.email}</span> shortly.
          </p>

          <div className="bg-[#FAFAF7] border border-stone-200 p-6 mb-6 text-left">
            <p className="text-sm font-semibold text-gray-800 mb-3">Want your quote faster? Reach us directly:</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-3 px-5 transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.929-1.03zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/></svg>
                WhatsApp us
              </a>
              <a
                href={`tel:${supportTel}`}
                data-testid="call-cta"
                className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-800 hover:bg-gray-900 hover:text-white text-gray-900 font-medium py-3 px-5 transition-colors"
              >
                Call {supportPhone}
              </a>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={() => { setStep(1); setRequestResult(null); setQuote(null); }}
            className="rounded-none px-10 py-6 text-lg"
            data-testid="new-request-btn"
          >
            Submit another request
          </Button>
        </div>
      </div>
    );
  }

  // =============================================
  // STEP 1: Configuration Form
  // =============================================
  if (step === 1) {
    return (
      <div className="min-h-screen bg-white py-32">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3 tracking-tight">{instantQuoteEnabled ? 'Interior Design Quotation' : 'Request an Interior Design Quote'}</h1>
            <p className="text-base text-gray-500">{instantQuoteEnabled ? 'Module-based pricing powered by Z-Engine V3' : 'Tell us about your space \u2014 our design team will prepare a personalised quote and email it to you.'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Personal Info */}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <h3 className="text-lg font-bold mb-4" data-testid="section-personal">Your Details</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Full Name *</Label><Input className="rounded-none mt-1" data-testid="input-name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required /></div>
                <div><Label>Email *</Label><Input type="email" className="rounded-none mt-1" data-testid="input-email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required /></div>
                <div><Label>Phone *</Label><Input type="tel" className="rounded-none mt-1" data-testid="input-phone" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} required /></div>
                <div>
                  <Label>City</Label>
                  <LocationSearch
                    value={formData.city}
                    onChange={(val) => setFormData(prev => ({ ...prev, city: val }))}
                    onLocationSelect={(loc) => loc && setFormData(prev => ({ ...prev, city: loc.city || loc.name }))}
                    placeholder="Search city, town or village..."
                    inputClassName="rounded-none mt-1"
                    data-testid="input-city"
                  />
                </div>
                <div><Label>Carpet Area (sq ft, optional)</Label><Input type="number" className="rounded-none mt-1" data-testid="input-carpet-area" value={formData.carpet_area} onChange={e => setFormData({ ...formData, carpet_area: e.target.value })} placeholder="e.g. 1000" /></div>
                <div>
                  <Label>Property Type</Label>
                  <Select value={formData.property_type} onValueChange={v => setFormData({ ...formData, property_type: v })}>
                    <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
            </section>

            {/* OTP Verification (conditional — only when admin has enabled `otp_verification_enabled`) */}
            {otpRequired && (
              <section className="bg-emerald-50 border border-emerald-200 p-6" data-testid="otp-section">
                <h3 className="text-lg font-bold mb-2 text-emerald-800">Verify your phone</h3>
                <p className="text-sm text-emerald-700 mb-1">
                  We&rsquo;ll send a quick 6-digit code to your phone so you can securely view your quote and receive updates.
                </p>
                <p className="text-xs text-emerald-600 mb-4">
                  Takes just a few seconds &middot; No spam. Just your quote and helpful updates.
                </p>
                {!otpVerified ? (
                  <div className="flex flex-wrap items-end gap-3">
                    {!otpSent ? (
                      <Button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpSending || !formData.phone}
                        className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
                        data-testid="send-otp-btn"
                      >
                        {otpSending ? 'Sending...' : 'Send code'}
                      </Button>
                    ) : (
                      <>
                        <div className="flex-1 min-w-[200px]">
                          <Label>Enter code *</Label>
                          <Input
                            className="rounded-none mt-1 tracking-widest"
                            data-testid="input-otp"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="6-digit code"
                            inputMode="numeric"
                            maxLength={6}
                          />
                        </div>
                        <Button
                          type="button"
                          onClick={handleVerifyOtp}
                          disabled={otpVerifying || otpCode.length < 4}
                          className="rounded-none bg-emerald-700 hover:bg-emerald-800 text-white"
                          data-testid="verify-otp-btn"
                        >
                          {otpVerifying ? 'Verifying...' : 'Verify code'}
                        </Button>
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={otpSending}
                          className="text-sm text-emerald-700 underline hover:text-emerald-900"
                          data-testid="resend-otp-btn"
                        >
                          {otpSending ? 'Resending...' : 'Resend code'}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800" data-testid="otp-verified-badge">
                    <span>Phone +91 {otpVerifiedPhone} verified &mdash; you&rsquo;re all set.</span>
                  </div>
                )}
              </section>
            )}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <h3 className="text-lg font-bold" data-testid="section-tier">Select Your Tier</h3>
                <p className="text-xs text-stone-500">Tap any tier to expand inclusions &amp; upgrade highlights</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-stretch">
                {TIERS.map((t, idx) => {
                  const isActive = formData.variant === t.key;
                  const prevTier = idx > 0 ? TIERS[idx - 1].label : null;
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`tier-${t.key}`}
                      aria-pressed={isActive}
                      className={`relative p-4 pt-5 border-2 text-left transition-all flex flex-col h-full ${isActive ? 'border-emerald-600 bg-emerald-50 shadow-sm' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                      onClick={() => setFormData({ ...formData, variant: t.key })}
                    >
                      {t.badge && (
                        <span
                          data-testid={`tier-${t.key}-badge`}
                          className={`absolute -top-2 left-3 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-sm ${t.badge === 'Most Popular' ? 'bg-emerald-600 text-white' : 'bg-violet-600 text-white'}`}
                        >
                          {t.badge}
                        </span>
                      )}

                      <div className="flex items-start justify-between mb-1 gap-2">
                        <span className="font-bold text-sm">{t.label}</span>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            data-testid={`tier-${t.key}-price`}
                            className={`text-xs font-semibold ${isActive ? 'text-emerald-700' : 'text-stone-500'}`}
                            title="Relative price indicator"
                          >
                            {t.price_indicator}
                          </span>
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${isActive ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-500'}`}>{t.tag}</span>
                        </div>
                      </div>
                      <p className="text-xs text-stone-500 leading-snug">{t.desc}</p>

                      {/* What's Included */}
                      <div data-testid={`tier-${t.key}-inclusions`} className="mt-3">
                        <p className="text-[11px] font-semibold text-stone-700 uppercase tracking-wide mb-1.5">What&apos;s Included</p>
                        <ul className="space-y-1">
                          {(isActive ? t.inclusions : t.inclusions.slice(0, 4)).map((item, i) => (
                            <li key={i} className="flex items-start gap-1.5 text-[11px] text-stone-600 leading-snug">
                              <CheckCircle className={`w-3 h-3 mt-0.5 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-stone-400'}`} />
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                        {!isActive && t.inclusions.length > 4 && (
                          <p className="text-[10px] text-stone-400 mt-1.5 italic">+{t.inclusions.length - 4} more &mdash; tap to expand</p>
                        )}
                      </div>

                      {/* What Improves vs previous tier */}
                      {isActive && t.upgrades.length > 0 && (
                        <div
                          data-testid={`tier-${t.key}-upgrades`}
                          className="mt-3 pt-3 border-t border-emerald-200"
                        >
                          <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Upgrade over {prevTier}
                          </p>
                          <ul className="space-y-1">
                            {t.upgrades.map((item, i) => (
                              <li key={i} className="flex items-start gap-1.5 text-[11px] text-stone-700 leading-snug">
                                <ChevronUp className="w-3 h-3 mt-0.5 flex-shrink-0 text-emerald-600" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Appliance Inclusion Block */}
                      <div
                        data-testid={`tier-${t.key}-appliances`}
                        className={`mt-3 pt-3 border-t text-[11px] leading-snug ${isActive ? 'border-emerald-200' : 'border-stone-200'}`}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <CheckCircle className={`w-3 h-3 ${isActive ? 'text-emerald-600' : 'text-stone-400'}`} />
                          <span className="font-semibold text-stone-700">Customizable Appliances</span>
                        </div>
                        <p className="text-stone-500">{t.appliance_support}</p>
                        <p className="text-stone-400 mt-1 italic">Customizable during the design phase based on your preferences.</p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div
                data-testid="appliances-policy-note"
                className="mt-4 flex items-start gap-2 bg-emerald-50/60 border border-emerald-200 px-3 py-2 text-xs text-stone-600"
              >
                <Info className="w-3.5 h-3.5 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span>
                  <strong className="text-stone-800">Appliances:</strong> Selected and added during your design consultation based on
                  preferences &mdash; flexible across every tier so the base quote stays transparent.
                </span>
              </div>

              <p
                data-testid="tier-pricing-disclaimer"
                className="mt-2 text-[11px] text-stone-400 italic"
              >
                Final pricing may vary based on design customization during design consultation.
              </p>
            </section>

            {/* House-Level Configuration */}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <h3 className="text-lg font-bold mb-4" data-testid="section-house-config">House-Level Configuration</h3>
              <div>
                <Label>False Ceiling (total sq ft across all rooms)</Label>
                <Input type="number" min="0" className="rounded-none mt-1 max-w-sm" data-testid="input-false-ceiling"
                  value={formData.false_ceiling_sqft} placeholder="e.g. 500 (leave empty if not needed)"
                  onChange={e => setFormData({ ...formData, false_ceiling_sqft: e.target.value })} />
                <p className="text-xs text-stone-400 mt-1">Total false ceiling area for the entire home. Rate depends on tier selection.</p>
              </div>
            </section>

            {/* Rooms */}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" data-testid="section-rooms">Rooms & Modules</h3>
                <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" data-testid="add-room-btn" onClick={() => addRoom('')}>
                  <Plus className="w-3 h-3 mr-1" /> Add Room
                </Button>
              </div>

              {formData.rooms.length === 0 && (
                <div className="text-center py-10 text-stone-400 border border-dashed border-stone-300 bg-white">
                  <p className="mb-3">No rooms added yet. Start by adding rooms and their furniture modules.</p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    {QUICK_ROOMS.map((qr, i) => (
                      <Button key={qr.name} type="button" variant="outline" size="sm" className="rounded-none text-xs"
                        data-testid={`quick-room-${qr.name.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => addRoom(qr.name, qr.type)}>{qr.name}</Button>
                    ))}
                  </div>
                </div>
              )}

              {formData.rooms.map((room, roomIdx) => (
                <RoomCard key={roomIdx} room={room} roomIdx={roomIdx}
                  onUpdateRoom={updateRoom} onRemoveRoom={removeRoom}
                  onAddKitchen={addKitchenToRoom} moduleOptions={moduleOptions} />
              ))}

              {formData.rooms.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
                  <div className="flex gap-2 flex-wrap">
                    {QUICK_ROOMS.map((qr, i) => (
                      <Button key={qr.name} type="button" variant="outline" size="sm" className="rounded-none text-xs"
                        data-testid={`quick-room-bottom-${qr.name.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => addRoom(qr.name, qr.type)}>{qr.name}</Button>
                    ))}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" data-testid="add-room-bottom-btn" onClick={() => addRoom('')}>
                    <Plus className="w-3 h-3 mr-1" /> Add Room
                  </Button>
                </div>
              )}
            </section>

            {/* Optional Project Add-ons */}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <h3 className="text-lg font-bold mb-2" data-testid="section-addons">Optional Project Add-ons</h3>
              <p className="text-xs text-stone-400 mb-4">These are not included by default. Enable only the items you need estimated in this quote.</p>
              <div className="grid md:grid-cols-2 gap-3">
                {PROJECT_ADDONS.map(addon => (
                  <label key={addon.key}
                    className={`flex items-start gap-3 p-4 border cursor-pointer transition-all ${formData.project_addons[addon.key] ? 'border-emerald-500 bg-emerald-50' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                    data-testid={`addon-toggle-${addon.key}`}>
                    <input type="checkbox" className="mt-0.5 rounded"
                      checked={formData.project_addons[addon.key]}
                      onChange={() => toggleAddon(addon.key)} />
                    <div>
                      <span className="font-medium text-sm">{addon.label}</span>
                      <p className="text-xs text-stone-500 mt-0.5">{addon.desc}</p>
                    </div>
                  </label>
                ))}
              </div>
            </section>

            {/* Notes */}
            <section className="bg-[#FAFAF7] border border-stone-200 p-6">
              <Label>Additional Notes (Optional)</Label>
              <Textarea className="rounded-none mt-1" rows={3} placeholder="Color preferences, special requirements..." />
            </section>

            <Button type="submit" data-testid="generate-quote-btn" className="w-full rounded-none bg-emerald-700 hover:bg-emerald-800 py-6 text-lg"
              disabled={loading || formData.rooms.length === 0}>
              {loading
                ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{instantQuoteEnabled ? 'Generating Quote...' : 'Submitting Request...'}</>
                : <><Sparkles className="w-5 h-5 mr-2" />{instantQuoteEnabled ? 'Generate Quote' : 'Request a Quote'}</>}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  // =============================================
  // STEP 2: Quote Results — 3-Layer Breakdown
  // =============================================
  const cli = quote?.customer_line_items || {};
  const df = quote?.derived_fees || {};
  const ab = quote?.addon_breakdown || {};
  const hasAddons = Object.keys(ab).length > 0;

  return (
    <div className="min-h-screen bg-white py-32">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2">Your Quote is Ready</h1>
          <p className="text-base text-gray-500">Quote ID: {quote.quote_id} &middot; {quote.tier_label} (Tier {quote.tier})</p>
        </div>

        {/* Total Banner */}
        <div className="bg-emerald-800 text-white p-6 mb-6 flex justify-between items-center" data-testid="quote-total-banner">
          <div>
            <p className="text-sm text-emerald-200">Total Project Cost (incl. GST)</p>
            <p className="text-3xl font-bold font-mono">{INR(quote.total_bill)}</p>
          </div>
          <div className="text-right text-sm text-emerald-200">
            <p>{quote.city} &middot; {quote.tier_label}</p>
            <p>{quote.carpet_area > 0 ? `${Math.round(quote.carpet_area)} sqft` : ''}</p>
          </div>
        </div>

        {/* ============ LAYER 1: Module Costs ============ */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" data-testid="section-module-costs">Module Costs</h2>
            <span className="font-mono text-emerald-700 font-bold text-lg">{INR(cli.module_total)}</span>
          </div>

          <div className="space-y-4">
            {quote.room_breakdowns?.map((rb, idx) => (
              <div key={idx} className="bg-[#FAFAF7] border border-stone-200 p-6" data-testid={`room-breakdown-${idx}`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold">{rb.room_name}</h3>
                  <span className="font-mono text-emerald-700 font-bold">{INR(rb.room_total)}</span>
                </div>

                {rb.modules_breakdown?.map((mod, mIdx) => (
                  <div key={mIdx} className="mb-5 pb-4 border-b border-stone-200 last:border-b-0 last:pb-0 last:mb-0" data-testid={`module-breakdown-${idx}-${mIdx}`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-sm capitalize">{mod.module_type?.replace(/_/g, ' ')}</h4>
                      <span className="text-sm font-mono text-emerald-700">{INR(mod.total_cost)}</span>
                    </div>

                    {mod.layout && mod.module_type === 'wardrobe' && (
                      <div className="mb-3">
                        <WardrobeLayoutPreview layout={mod.layout} totalWidth={mod.dimensions?.width} height={mod.dimensions?.height} />
                      </div>
                    )}

                    {mod.module_type === 'kitchen' && mod.sections && (
                      <div className="mb-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {mod.sections.lower_cabinets && <div className="bg-white border border-stone-200 p-2"><span className="text-stone-500 block">Lower Cabinets</span><span className="font-mono">{INR(mod.sections.lower_cabinets.cost)}</span></div>}
                        {mod.sections.upper_cabinets && <div className="bg-white border border-stone-200 p-2"><span className="text-stone-500 block">Upper Cabinets</span><span className="font-mono">{INR(mod.sections.upper_cabinets.cost)}</span></div>}
                        {mod.sections.countertop && <div className="bg-white border border-stone-200 p-2"><span className="text-stone-500 block">Countertop</span><span className="font-mono">{INR(mod.sections.countertop.total)}</span></div>}
                        {mod.sections.tall_units && <div className="bg-white border border-stone-200 p-2"><span className="text-stone-500 block">Tall Units ({mod.sections.tall_units.count})</span><span className="font-mono">{INR(mod.sections.tall_units.total_cost)}</span></div>}
                        {mod.sections.loft && <div className="bg-white border border-stone-200 p-2"><span className="text-stone-500 block">Loft</span><span className="font-mono">{INR(mod.sections.loft.cost)}</span></div>}
                      </div>
                    )}

                    {mod.costBreakdown && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-xs">
                        {Object.entries(mod.costBreakdown).filter(([, v]) => v > 0).map(([k, v]) => (
                          <div key={k} className="flex justify-between">
                            <span className="text-stone-500 capitalize">{k.replace(/_/g, ' ')}</span>
                            <span className="font-mono">{INR(v)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {rb.panels_breakdown?.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-stone-200">
                    <h4 className="font-semibold text-sm mb-1">Paneling</h4>
                    {rb.panels_breakdown.map((p, pi) => (
                      <div key={pi} className="flex justify-between text-xs"><span className="text-stone-500">{p.area} sqft ({p.complexity})</span><span className="font-mono">{INR(p.total)}</span></div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ============ LAYER 2: Derived Fees ============ */}
        <div className="bg-white border border-stone-200 p-6 mb-6" data-testid="derived-fees-section">
          <h2 className="text-xl font-bold mb-4">Service Fees</h2>
          <div className="space-y-2">
            {[
              ['Execution & Site Mgmt', df.execution],
              ['Management Fee', df.management_fee],
              ['Contingency', df.contingency],
              ['Designer Fee', df.designer_fee],
            ].map(([label, item]) => item && (
              <div key={label} className="flex justify-between text-sm">
                <span className="text-stone-600">{label} {item.percent ? `(${item.percent}%)` : ''}</span>
                <span className="font-mono">{INR(item.amount)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t pt-2 mt-2 font-medium">
              <span className="text-stone-700">Total Service Fees</span>
              <span className="font-mono">{INR(cli.derived_fees_total)}</span>
            </div>
          </div>
        </div>

        {/* ============ LAYER 3: Add-ons (only if enabled) ============ */}
        {hasAddons && (
          <div className="bg-blue-50 border border-blue-200 p-6 mb-6" data-testid="addons-section">
            <div className="flex items-center gap-2 mb-4">
              <Info className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-bold text-blue-900">Optional Add-ons</h2>
            </div>
            <div className="space-y-2">
              {Object.entries(ab).map(([key, item]) => {
                const label = key === 'electrical'
                  ? 'Electrical & Lighting (Provisional)'
                  : key.replace(/_/g, ' ');
                return (
                  <div key={key} className="flex justify-between text-sm" data-testid={`addon-row-${key}`}>
                    <span className="text-stone-700 capitalize">{label}</span>
                    <span className="font-mono">{item.cost > 0 ? INR(item.cost) : 'TBD'}</span>
                  </div>
                );
              })}
              <div className="flex justify-between border-t border-blue-200 pt-2 mt-2 font-medium">
                <span className="text-blue-900">Total Add-ons</span>
                <span className="font-mono">{INR(cli.addons_total)}</span>
              </div>
            </div>

            {ab.electrical?.is_provisional && (
              <div className="mt-4 bg-amber-50 border border-amber-300 p-4 rounded" data-testid="electrical-provisional-note">
                <h3 className="text-sm font-bold text-amber-900 mb-1">Electrical & Lighting (Provisional Estimate)</h3>
                <p className="text-xs text-amber-800 mb-2">
                  This quotation includes a basic electrical provision calculated at
                  &nbsp;<span className="font-semibold">{`\u20B9${ab.electrical.base_rate_per_sqft}/sq.ft`}</span>,
                  covering standard wiring, basic switches, and installation.
                </p>
                {ab.electrical.excludes?.length > 0 && (
                  <>
                    <p className="text-xs text-amber-900 font-semibold mb-1">Please note the following are not included:</p>
                    <ul className="list-disc pl-5 text-xs text-amber-800 space-y-0.5 mb-2">
                      {ab.electrical.excludes.map((e, i) => (
                        <li key={i} className="capitalize">{e}</li>
                      ))}
                    </ul>
                  </>
                )}
                <p className="text-xs text-amber-800 italic">
                  A detailed electrical plan and final costing will be provided during the design consultation.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ============ Grand Total ============ */}
        <div className="bg-white border border-stone-200 p-6 mb-6" data-testid="project-cost-summary">
          <h2 className="text-xl font-bold mb-4">Project Total</h2>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">Module Costs</span>
              <span className="font-mono">{INR(cli.module_total)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-stone-600">Service Fees</span>
              <span className="font-mono">{INR(cli.derived_fees_total)}</span>
            </div>
            {hasAddons && (
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Add-ons</span>
                <span className="font-mono">{INR(cli.addons_total)}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-stone-600">Subtotal before GST</span>
              <span className="font-mono font-bold">{INR(cli.subtotal_before_gst)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-stone-600">GST (18%)</span>
              <span className="font-mono">{INR(cli.gst)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-lg font-bold">Total Bill</span>
              <span className="font-mono text-2xl text-emerald-700 font-bold">{INR(cli.total_bill)}</span>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6">
          <div className="flex"><AlertCircle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0" /><p className="text-sm text-amber-800">{quote.disclaimer}</p></div>
        </div>

        {/* Actions */}
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-4">Quote valid for 30 days.</p>
          {bookingConfirmed ? (
            <div className="bg-green-50 border border-green-200 p-8 mb-6">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-green-800 mb-1">Consultation Booked!</h3>
              <p className="text-green-700">Our design team will contact you within 24-48 hours.</p>
            </div>
          ) : (
            <div className="flex gap-4 justify-center">
              <Button onClick={async () => {
                const user = getUser();
                if (!user) { toast.error('Please login to book'); navigate('/login'); return; }
                setBookingPayment(true);
                try {
                  await initiateServicePayment({
                    amount: consultationFee, description: 'Interior Design Consultation',
                    userName: user.name || user.full_name, userEmail: user.email, userPhone: user.phone,
                    metadata: { type: 'interior_design_consultation', variant: formData.variant },
                    onSuccess: () => { setBookingConfirmed(true); toast.success('Consultation booked!'); },
                    onFailure: (d) => toast.error(d.error || 'Payment failed'),
                    onDismiss: () => toast.info('Payment cancelled'),
                  });
                } catch (err) { if (err.message !== 'Payment cancelled by user') toast.error(err.message); }
                finally { setBookingPayment(false); }
              }} disabled={bookingPayment || pricingLoading}
                className="rounded-none bg-emerald-700 hover:bg-emerald-800 px-10 py-6 text-lg" data-testid="book-consultation-btn">
                {bookingPayment ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : <>Book Consultation - {INR(consultationFee)}</>}
              </Button>
              <Button variant="outline" onClick={() => { setStep(1); setQuote(null); }} className="rounded-none px-10 py-6 text-lg" data-testid="new-quote-btn">New Quote</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// =============================================
// ROOM CARD COMPONENT
// =============================================
const RoomCard = ({ room, roomIdx, onUpdateRoom, onRemoveRoom, onAddKitchen, moduleOptions }) => {
  const [expanded, setExpanded] = useState(true);
  const isKitchenRoom = room.room_type === 'kitchen';
  const hasKitchen = room.modules.some(m => m.type === 'kitchen');
  const allowedModules = ROOM_MODULE_MAP[room.room_type] || ROOM_MODULE_MAP.other;
  const nonKitchenAllowed = allowedModules.filter(m => m.key !== 'kitchen');

  return (
    <div className="bg-white border border-stone-200 mb-3" data-testid={`room-card-${roomIdx}`}>
      <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-50" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          <Input className="rounded-none border-none p-0 h-auto font-bold text-base bg-transparent focus-visible:ring-0" data-testid={`room-name-${roomIdx}`}
            value={room.name} onClick={e => e.stopPropagation()}
            onChange={e => onUpdateRoom(roomIdx, 'name', e.target.value)} />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-stone-400 bg-stone-100 px-2 py-0.5 rounded">
            {ROOM_TYPES.find(rt => rt.key === room.room_type)?.label || 'Other'}
          </span>
          <span className="text-xs text-stone-400">{room.modules.length} module{room.modules.length !== 1 ? 's' : ''}</span>
          <Button variant="ghost" size="sm" data-testid={`remove-room-${roomIdx}`} onClick={e => { e.stopPropagation(); onRemoveRoom(roomIdx); }}>
            <Trash2 className="w-4 h-4 text-red-400" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-stone-100 p-4 space-y-4">
          {/* Room Type Selector */}
          <div className="flex items-center gap-3">
            <Label className="text-xs text-stone-500 whitespace-nowrap">Room Type</Label>
            <Select value={room.room_type || 'other'} onValueChange={v => onUpdateRoom(roomIdx, 'room_type', v)}>
              <SelectTrigger className="rounded-none w-48 text-xs" data-testid={`room-type-select-${roomIdx}`} onClick={e => e.stopPropagation()}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROOM_TYPES.map(rt => (
                  <SelectItem key={rt.key} value={rt.key}>{rt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Kitchen module management */}
          {isKitchenRoom && (
            <>
              {!hasKitchen && (
                <Button type="button" variant="outline" size="sm" className="rounded-none text-xs" data-testid={`add-kitchen-${roomIdx}`}
                  onClick={() => onAddKitchen(roomIdx)}>
                  <Plus className="w-3 h-3 mr-1" /> Add Kitchen Module
                </Button>
              )}
              {room.modules.filter(m => m.type === 'kitchen').map((km, ki) => {
                const kmIdx = room.modules.indexOf(km);
                return (
                  <KitchenModuleCard key={ki} module={km}
                    onUpdate={(field, val) => {
                      const mods = [...room.modules]; mods[kmIdx] = { ...mods[kmIdx], [field]: val };
                      onUpdateRoom(roomIdx, 'modules', mods);
                    }}
                    onUpdateMany={(patch) => {
                      const mods = [...room.modules]; mods[kmIdx] = { ...mods[kmIdx], ...patch };
                      onUpdateRoom(roomIdx, 'modules', mods);
                    }}
                    onRemove={() => {
                      onUpdateRoom(roomIdx, 'modules', room.modules.filter((_, i) => i !== kmIdx));
                    }} />
                );
              })}
            </>
          )}

          {/* Non-kitchen modules */}
          {!isKitchenRoom && (
            <ModuleConfigurator
              modules={room.modules.filter(m => m.type !== 'kitchen')}
              onModulesChange={newMods => {
                const kitchenMods = room.modules.filter(m => m.type === 'kitchen');
                onUpdateRoom(roomIdx, 'modules', [...kitchenMods, ...newMods]);
              }}
              panels={room.panels}
              onPanelsChange={p => onUpdateRoom(roomIdx, 'panels', p)}
              options={moduleOptions}
              roomType={room.room_type}
              allowedModules={nonKitchenAllowed}
            />
          )}
        </div>
      )}
    </div>
  );
};

// =============================================
// KITCHEN MODULE CARD
// =============================================
const KITCHEN_QUALITY_TIERS = [
  { key: 'basic', label: 'Basic', activeCls: 'border-stone-500 bg-stone-100' },
  { key: 'standard', label: 'Standard', activeCls: 'border-sky-500 bg-sky-100' },
  { key: 'premium', label: 'Premium', activeCls: 'border-amber-500 bg-amber-100' },
  { key: 'luxury', label: 'Luxury', activeCls: 'border-violet-500 bg-violet-100' },
];

const KitchenModuleCard = ({ module, onUpdate, onUpdateMany, onRemove }) => {
  const [expanded, setExpanded] = useState(true);
  const layout = module.layout || 'L';
  const lengths = module.lengths || [8, 6];
  const activeTier = module.quality_tier || 'standard';

  const updateLength = (idx, val) => {
    const newLengths = [...lengths];
    newLengths[idx] = parseFloat(val) || 0;
    onUpdate('lengths', newLengths);
  };

  const setLayout = (newLayout) => {
    const runCounts = { Straight: 1, L: 2, Parallel: 2, U: 3 };
    const count = runCounts[newLayout] || 2;
    const currentLengths = lengths;
    const newLengths = Array(count).fill(8).map((v, i) => currentLengths[i] || v);
    // Single batched update to avoid stale-closure races between layout and lengths.
    onUpdateMany({ layout: newLayout, lengths: newLengths.slice(0, count) });
  };

  return (
    <div className="bg-amber-50 border border-amber-200 p-4" data-testid="kitchen-module-card">
      <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          <span className="font-bold text-sm">Kitchen Module</span>
          <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded">{layout}-Shape</span>
          <span className="text-xs text-stone-600 bg-stone-100 px-2 py-0.5 rounded capitalize">{activeTier}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); onRemove(); }}><Trash2 className="w-4 h-4 text-red-400" /></Button>
      </div>

      {expanded && (
        <div className="space-y-3">
          {/* Quality Tier Selector */}
          <div>
            <Label className="text-xs text-stone-500 mb-1 block">Quality Tier</Label>
            <div className="grid grid-cols-4 gap-2">
              {KITCHEN_QUALITY_TIERS.map(t => {
                const isActive = activeTier === t.key;
                return (
                  <button key={t.key} type="button" data-testid={`kitchen-tier-${t.key}`}
                    className={`p-2 border text-center text-xs transition-all ${isActive ? `${t.activeCls} font-bold` : 'border-stone-200 bg-white hover:border-stone-300'}`}
                    onClick={() => onUpdate('quality_tier', t.key)}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs text-stone-500 mb-1 block">Layout</Label>
            <div className="grid grid-cols-4 gap-2">
              {KITCHEN_LAYOUTS.map(l => (
                <button key={l.key} type="button" data-testid={`kitchen-layout-${l.key}`}
                  className={`p-2 border text-center text-xs transition-all ${layout === l.key ? 'border-amber-500 bg-amber-100 font-bold' : 'border-stone-200 bg-white hover:border-stone-300'}`}
                  onClick={() => setLayout(l.key)}>
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-stone-500 mb-1 block">Run Lengths (feet)</Label>
            <div className="flex gap-2">
              {lengths.map((len, i) => (
                <div key={i} className="flex-1">
                  <Label className="text-[10px] text-stone-400">Run {i + 1}</Label>
                  <Input type="number" step="0.5" min="3" max="20" className="rounded-none mt-0.5 font-mono text-sm" data-testid={`kitchen-run-${i}`}
                    value={len} onChange={e => updateLength(i, e.target.value)} />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-stone-500">Countertop</Label>
              <Select value={module.countertop || 'Basic_Granite'} onValueChange={v => onUpdate('countertop', v)}>
                <SelectTrigger className="rounded-none mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Basic_Granite">Basic Granite</SelectItem>
                  <SelectItem value="Level_1_Granite">Level 1 Granite</SelectItem>
                  <SelectItem value="Level_2_Granite">Level 2 Granite</SelectItem>
                  <SelectItem value="Quartz_Standard">Standard Quartz</SelectItem>
                  <SelectItem value="Premium_Quartz">Premium Quartz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Tall Units</Label>
              <Input type="number" min="0" max="4" className="rounded-none mt-1 font-mono text-sm" data-testid="kitchen-tall-units"
                value={module.num_tall_units || 0} onChange={e => onUpdate('num_tall_units', parseInt(e.target.value) || 0)} />
            </div>
            <div className="flex items-end gap-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={module.loft || false} onChange={e => onUpdate('loft', e.target.checked)} className="rounded" data-testid="kitchen-loft" />
                Loft Storage
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InteriorDesignZEngine;
